(ns hai.mini-apps.card
  "Mini-app card. Each visual concept lives in its own small styled
   component so the render fn reads like markup and CSS rules stay local.
   Shared scaffolding (sharing block, scope marks, icon frame) lives in
   [[hai.web-app.entity-card]]."
  (:require
   hai.mini-apps.common
   [hai.mini-apps.icons :as mini-apps-icons]
   [hai.ui.components.card :as ui-card]
   [hai.ui.components.collection.views.cards :as cards]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [>e]]
   [hai.ui.tokens :as t]
   [hai.web-app.entity-card :as entity-card]
   [lambdaisland.ornament :as o]))

;; ---------------------------------------------------------------------------
;; Container
;; ---------------------------------------------------------------------------

(o/defstyled mini-app-card :article
  {:display "grid"
   :grid-template-rows "auto 1fr auto"
   :gap "14px"
   :padding "18px"
   :min-height "148px"
   :border-radius "14px"
   :background t/--card-bg
   :border "1px solid var(--card-forest-70)"
   :box-shadow "0 1px 0 var(--card-ink-04), 0 1px 2px var(--card-ink-04)"
   :transition "background .15s ease, border-color .15s ease, box-shadow .15s ease"
   :cursor "pointer"
   :position "relative"}
  ;; Bulk-action checkbox lives in `ui-card/edit-overlay` (absolute top-right).
  ;; Its default opacity 0 is meant to reveal on parent hover via the
  ;; `card-hover` mixin; we don't use that mixin so force it visible here.
  [ui-card/edit-overlay {:opacity "1"}]
  [:&:hover
   {:box-shadow "0 1px 0 var(--card-ink-04), 0 6px 18px -10px var(--card-ink-18)"}]
  [:&:focus-visible
   {:outline "2px solid var(--card-ink)"
    :outline-offset "2px"}])

;; ---------------------------------------------------------------------------
;; Head row — icon, title, optional external-link
;; ---------------------------------------------------------------------------

(o/defstyled head-row :div
  ;; Reserve the top-right corner for the bulk-select checkbox; without
  ;; the padding the title (especially when it carries the open-in-new
  ;; glyph) collides with the checkbox.
  {:display "flex" :align-items "center" :gap "12px"
   :padding-right "32px"})

(o/defstyled card-title :h3
  {:flex "1" :min-width "0"
   :margin "0"
   :font-size "15px" :font-weight "600" :line-height "1.25"
   :letter-spacing "-0.005em"
   :color t/--card-ink
   :display "-webkit-box"
   :-webkit-line-clamp "2"
   :-webkit-box-orient "vertical"
   :overflow "hidden"})

;; Link variant: rendered when the mini-app has a `:source-url`. Title text
;; carries an inline open-in-new glyph after it and behaves like a link.
;; Card body click still goes to the edit page; this link goes to the source.
(o/defstyled card-title-link :a
  {:flex "1" :min-width "0"
   :margin "0"
   :font-size "15px" :font-weight "600" :line-height "1.25"
   :letter-spacing "-0.005em"
   :color t/--card-ink
   :text-decoration "none"
   :display "-webkit-box"
   :-webkit-line-clamp "2"
   :-webkit-box-orient "vertical"
   :overflow "hidden"
   :cursor "pointer"
   :transition "color .15s ease"}
  [:&:hover {:color t/--card-ink-hover :text-decoration "underline"}]
  ;; Inline trailing glyph — sits right after the title text and wraps with
  ;; it when the title runs to two lines.
  [:.external-glyph
   {:display "inline-flex" :vertical-align "-2px"
    :margin-left "4px"
    :color t/--card-text-muted}
   [:svg {:width "14px" :height "14px"}]])

;; ---------------------------------------------------------------------------
;; Status row — dot + Enabled/Disabled + optional Beta pill
;; ---------------------------------------------------------------------------

(o/defstyled status-row :div
  {:display "flex" :align-items "center" :gap "8px"})

(o/defstyled status-dot :span
  {:width "6px" :height "6px"
   :flex-shrink "0"
   :border-radius "50%"
   :background t/--card-track
   :transition "background .15s ease, box-shadow .15s ease"}
  [:&.on
   {:background t/--card-forest
    :box-shadow "0 0 0 3px var(--card-forest-10)"}])

(o/defstyled status-label :span
  {:font-size "12px" :font-weight "500"
   :color t/--card-text-muted}
  [:&.on {:color t/--card-forest}])

(o/defstyled beta-pill :span
  {:display "inline-flex" :align-items "center"
   :height "20px"
   :padding "0 8px"
   :margin-left "6px"
   :border-radius "5px"
   :background t/--card-violet-soft
   :color t/--card-ink
   :font-size "11px" :font-weight "600" :letter-spacing "0.01em"})

;; ---------------------------------------------------------------------------
;; Foot row — sharing stack (label + names) on the left, toggle on the right
;; ---------------------------------------------------------------------------

(o/defstyled foot-row :div
  {:display "flex" :align-items "center" :justify-content "space-between"
   :gap "12px"
   ;; Extra breathing room on top so the controls don't crowd the status
   ;; row above; the card's grid gap already sets the baseline rhythm.
   :margin-top "10px"})

(o/defstyled sharing-stack :div
  {:display "flex" :flex-direction "column" :align-items "flex-start" :gap "4px"
   :min-width "0" :flex "1 1 auto"})

;; ---------------------------------------------------------------------------
;; Toggle
;; ---------------------------------------------------------------------------

(o/defstyled toggle-switch :button
  {:position "relative"
   :flex-shrink "0"
   :width "38px" :height "22px"
   :border "none"
   :border-radius "999px"
   :background t/--card-track
   :cursor "pointer"
   :transition "background .18s ease"
   :padding "0"}
  [:&.on {:background t/--card-forest}]
  [:.knob
   {:position "absolute"
    :top "2px" :left "2px"
    :width "18px" :height "18px"
    :border-radius "50%"
    :background t/--colour-white
    :box-shadow "0 1px 2px rgba(0,0,0,0.18)"
    :transition "transform .18s ease"}]
  [:&.on [:.knob {:transform "translateX(16px)"}]])

;; ---------------------------------------------------------------------------
;; Top-level card render
;; ---------------------------------------------------------------------------

(defn- event-from-self?
  "React bubbles synthetic events through the component tree, so a click
   inside a portaled child (e.g. a Radix dialog overlay) reaches our
   handler even though the DOM target lives outside our subtree. Filter
   those out by checking real DOM containment."
  [e]
  (.contains (.-currentTarget e) (.-target e)))

(defn- update-active! [collection uuid next-value]
  (>e [:resolve
       [(list 'mini-app/update
              {:mini-app/uuid uuid
               :mini-app/fields-update {:mini-app/active? next-value}})]
       {:on-success-fx [[:dispatch [:table/resolve collection]]
                        [:browser-ext/notify-mini-apps-changed {:mini-app/uuids [uuid] :change :updated}]]}]))

(defn render
  [collection row]
  (let [{:mini-app/keys [uuid title tool active? beta? source-url]
         :as item} (:form/current-value row)
        icon (get mini-apps-icons/tool->icon tool)
        on   (boolean active?)
        open-detail (fn [] (>e [:routing/navigate
                                [:admin-ui/mini-app-edit {:mini-app/uuid uuid}]]))]
    [mini-app-card
     {:tab-index 0
      :on-click (fn [e]
                  (when (event-from-self? e)
                    (open-detail)))
      :on-key-down (fn [e]
                     (when (and (#{"Enter" " "} (.-key e))
                                (= (.-currentTarget e) (.-target e)))
                       (.preventDefault e)
                       (open-detail)))}
     [cards/card-select-checkbox collection row]
     [head-row
      [entity-card/icon-frame (when icon [icon])]
      (if source-url
        [card-title-link
         {:href source-url
          :target "_blank"
          :rel "noopener noreferrer"
          :aria-label (str "Open " title " in new tab")
          :on-click #(.stopPropagation %)}
         title
         [:span.external-glyph [icons/outlined-open_in_new]]]
        [card-title title])]
     [status-row
      [status-dot {:class (when on "on")}]
      [status-label {:class (when on "on")}
       (if on "Enabled" "Disabled")]
      (when beta? [beta-pill "Beta"])]
     [foot-row
      [sharing-stack
       [entity-card/sharing-block
        {:collection         collection
         :entity             item
         :aria-label         "Share mini-app"
         :dialog-description "People with access to this mini-app"}]]
      [toggle-switch
       {:type "button"
        :role "switch"
        :aria-checked (str on)
        :aria-label (if on "Disable mini-app" "Enable mini-app")
        :class (when on "on")
        :on-click (fn [e]
                    (.stopPropagation e)
                    (update-active! collection uuid (not on)))}
       [:span.knob]]]]))
